import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { Address } from '../addresses/Address.js';
import { UitpasEventResponse } from '../endpoints/UitpasEventsResponse.js';
import { Image } from '../files/Image.js';
import { ReservedSeat } from '../SeatingPlan.js';
import { WebshopField } from './WebshopField.js';
import { upgradePriceFrom2To4DecimalPlaces } from '../upgradePriceFrom2To4DecimalPlaces.js';
export class ProductPrice extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = '';
        /**
         * Price is always in cents, to avoid floating point errors
         */
        this.price = 0;
        // Optional: different price if you reach a given amount of pieces (options and prices shouldn't be the same)
        this.discountPrice = null;
        // Only used if discountPrice is not null
        this.discountAmount = 2;
        this.hidden = false;
        /**
         * Total stock, excluding already sold items into account
         */
        this.stock = null;
        this.usedStock = 0;
        /**
         * If this id is not null, this productPrice is the UiTPAS social tariff.
         * In that case it refers to the base price on which the UiTPAS social tariff is applied.
         * If it is null, this is not an UiTPAS social tariff.
         */
        this.uitpasBaseProductPriceId = null;
    }
    get isSoldOut() {
        if (this.stock === null) {
            return false;
        }
        return this.usedStock >= this.stock;
    }
    get remainingStock() {
        if (this.stock === null) {
            return null;
        }
        return Math.max(0, this.stock - this.usedStock);
    }
    clearStock() {
        this.usedStock = 0;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], ProductPrice.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], ProductPrice.prototype, "name", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], ProductPrice.prototype, "price", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 93 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], ProductPrice.prototype, "discountPrice", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 93 })
], ProductPrice.prototype, "discountAmount", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 219 })
], ProductPrice.prototype, "hidden", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 221 })
], ProductPrice.prototype, "stock", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 221 })
], ProductPrice.prototype, "usedStock", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 376 })
], ProductPrice.prototype, "uitpasBaseProductPriceId", void 0);
export class Option extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = '';
        /**
         * Price added (can be negative) is always in cents, to avoid floating point errors
         */
        this.price = 0;
        /**
         * Total stock, excluding already sold items into account
         */
        this.stock = null;
        this.usedStock = 0;
    }
    get isSoldOut() {
        if (this.stock === null) {
            return false;
        }
        return this.usedStock >= this.stock;
    }
    get remainingStock() {
        if (this.stock === null) {
            return null;
        }
        return Math.max(0, this.stock - this.usedStock);
    }
    clearStock() {
        this.usedStock = 0;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Option.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Option.prototype, "name", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], Option.prototype, "price", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 221 })
], Option.prototype, "stock", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 221 })
], Option.prototype, "usedStock", void 0);
export class OptionMenu extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = '';
        this.multipleChoice = false;
        /**
         * By default, we select the first option if multipleChoise is false.
         */
        this.autoSelectFirst = true;
        this.options = [
            Option.create({}),
        ];
    }
    clearStock() {
        this.options.forEach(o => o.clearStock());
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], OptionMenu.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], OptionMenu.prototype, "name", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], OptionMenu.prototype, "multipleChoice", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 376 })
], OptionMenu.prototype, "autoSelectFirst", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Option) })
], OptionMenu.prototype, "options", void 0);
export var ProductType;
(function (ProductType) {
    ProductType["Product"] = "Product";
    ProductType["Person"] = "Person";
    ProductType["Ticket"] = "Ticket";
    ProductType["Voucher"] = "Voucher";
})(ProductType || (ProductType = {}));
/**
 * This includes a location for a ticket (will be visible on the ticket)
 */
export class ProductLocation extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = '';
        this.address = null;
        // TODO: coordinates here (only filled in by backend)
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], ProductLocation.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], ProductLocation.prototype, "name", void 0);
__decorate([
    field({ decoder: Address }),
    field({ decoder: Address, nullable: true, version: 146, downgrade: (v) => {
            if (!v) {
                return Address.createDefault();
            }
            return v;
        } })
], ProductLocation.prototype, "address", void 0);
/**
 * This includes a time for a ticket (will be visible on the ticket)
 */
export class ProductDateRange extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.startDate = new Date();
        this.endDate = new Date();
    }
    toString() {
        return Formatter.dateRange(this.startDate, this.endDate);
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], ProductDateRange.prototype, "id", void 0);
__decorate([
    field({ decoder: DateDecoder })
], ProductDateRange.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder })
], ProductDateRange.prototype, "endDate", void 0);
export class Product extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = '';
        this.description = '';
        this.enabled = true;
        this.hidden = false;
        /**
         * Allow to order multiple pieces of the same product combination
         */
        this.allowMultiple = true;
        /**
         * Only allow one piece per product combination
         */
        this.unique = false;
        this.enableAfter = null;
        this.disableAfter = null;
        this.images = [];
        this.customFields = [];
        this.type = ProductType.Product;
        this.location = null;
        this.dateRange = null;
        this.seatingPlanId = null;
        this.reservedSeats = [];
        /**
         * WIP: not yet supported
         * Set to true if you need to have a name for every ordered product. When this is true, you can't order this product mutliple times with the same name.
         * + will validate the name better
         */
        this.askName = false;
        /**
         * Maximum amount per order
         */
        this.maxPerOrder = null;
        /**
         * Only show available stock if going below this amount - or null to always show the stock.
         */
        this.showStockBelow = 25;
        /**
         * Total stock, excluding already sold items into account
         */
        this.stock = null;
        this.usedStock = 0;
        this.optionMenus = [];
        /**
         * If this id is not null, we use the official UiTPAS flow.
         * If it is null, we use the non-official flow (if one of the productPrices is UiTPAS social tariff
         */
        this.uitpasEvent = null;
    }
    get isEnabled() {
        return this.enabled && !this.hidden && (!this.enableAfter || this.enableAfter <= new Date()) && (!this.disableAfter || this.disableAfter >= new Date());
    }
    get enableInFuture() {
        return this.enabled && !this.hidden && this.enableAfter !== null && this.enableAfter > new Date() && (!this.disableAfter || this.disableAfter >= new Date());
    }
    get isTicket() {
        return this.type === ProductType.Ticket || this.type === ProductType.Voucher;
    }
    clearStock() {
        this.usedStock = 0;
        this.reservedSeats = [];
        this.optionMenus.forEach(o => o.clearStock());
        this.prices.forEach(p => p.clearStock());
    }
    get isSoldOut() {
        return this.remainingStockWithOptions === 0;
    }
    /**
     * Only accounts for the stock of the product itself, not the stock of the options
     */
    get remainingStock() {
        if (this.stock === null) {
            return null;
        }
        return Math.max(0, this.stock - this.usedStock);
    }
    /**
     * Accounts for options and prices too
     */
    get remainingStockWithOptions() {
        const stocks = [];
        if (this.remainingStock !== null) {
            stocks.push(this.remainingStock);
        }
        let priceStocks = [];
        for (const price of this.prices) {
            if (price.remainingStock !== null) {
                priceStocks.push(price.remainingStock);
            }
            else {
                // Infinite stock for at least one price = no price stock
                priceStocks = [];
                break;
            }
        }
        if (priceStocks.length) {
            stocks.push(Math.max(...priceStocks));
        }
        for (const menu of this.optionMenus) {
            if (!menu.multipleChoice) {
                // Required to pick one
                // We need to pick the maximum of the option stock
                let menuStocks = [];
                for (const option of menu.options) {
                    if (option.remainingStock !== null) {
                        menuStocks.push(option.remainingStock);
                    }
                    else {
                        // Infinite stock for at least one option = no menu stock
                        menuStocks = [];
                        break;
                    }
                }
                if (menuStocks.length) {
                    stocks.push(Math.max(...menuStocks));
                }
            }
        }
        if (stocks.length === 0) {
            return null;
        }
        return Math.min(...stocks);
    }
    get hasAnyStock() {
        if (this.remainingStock !== null) {
            return true;
        }
        for (const price of this.prices) {
            if (price.remainingStock !== null) {
                return true;
            }
        }
        for (const menu of this.optionMenus) {
            // Required to pick one
            // We need to pick the maximum of the option stock
            for (const option of menu.options) {
                if (option.remainingStock !== null) {
                    return true;
                }
            }
        }
        return false;
    }
    getRemainingSeats(webshop, isAdmin) {
        if (this.seatingPlanId === null) {
            return null;
        }
        const seatingPlan = webshop.meta.seatingPlans.find(p => p.id === this.seatingPlanId);
        if (!seatingPlan) {
            return null;
        }
        if (isAdmin) {
            return seatingPlan.seatCount - this.reservedSeats.length;
        }
        return seatingPlan.seatCount - seatingPlan.adminSeatCount - this.reservedSeats.filter(r => !seatingPlan.isAdminSeat(r)).length;
    }
    /**
     * Whether it is not possibel to add multiple different items of this product to the cart, or whether this product supports multiple items in the cart.
     * Controls whether cart is edited by default or updated when clicking it open in the webshop.
     */
    get isUnique() {
        if (this.maxPerOrder === 1) {
            return true;
        }
        // No choice options
        if (this.optionMenus.length === 0 && this.prices.length <= 1 && this.customFields.length === 0) {
            return true;
        }
        return false;
    }
    getRemainingStockText(stock) {
        if (stock === 1) {
            if (this.type === ProductType.Ticket) {
                return $t(`%t9`);
            }
            if (this.type === ProductType.Person) {
                return $t(`%tA`);
            }
            return $t(`%tB`);
        }
        if (this.type === ProductType.Ticket) {
            return stock + ' ' + $t(`%m`);
        }
        if (this.type === ProductType.Person) {
            return stock + ' ' + $t(`%12R`);
        }
        return stock + ' ' + $t(`%12S`);
    }
    get stockText() {
        if (this.remainingStockWithOptions === null || this.remainingStockWithOptions > 25) {
            return null;
        }
        if (this.remainingStockWithOptions === 0) {
            return $t(`%12p`);
        }
        return $t(`%Un`) + ' ' + this.getRemainingStockText(this.remainingStockWithOptions);
    }
    get isEnabledTextLong() {
        if (this.hidden) {
            return $t(`%UC`);
        }
        if (!this.enabled) {
            return $t(`%Tk`);
        }
        if (this.enableInFuture && this.enableAfter) {
            if (this.disableAfter) {
                return $t(`%tC`) + ' ' + Formatter.dateTime(this.enableAfter) + ' ' + $t(`%10C`) + ' ' + Formatter.dateTime(this.disableAfter);
            }
            return $t(`%tC`) + ' ' + Formatter.dateTime(this.enableAfter);
        }
        if (!this.isEnabled) {
            return $t(`%Tk`);
        }
        if (this.disableAfter) {
            return $t(`%tD`) + ' ' + Formatter.dateTime(this.disableAfter);
        }
    }
    get closesSoonText() {
        if (!this.isEnabled) {
            return null;
        }
        if (this.disableAfter) {
            const diff = this.disableAfter.getTime() - new Date().getTime();
            if (diff < 24 * 60 * 60 * 1000) {
                return $t(`%tD`) + ' ' + Formatter.time(this.disableAfter);
            }
        }
        return null;
    }
    filteredPrices(options) {
        if (options.admin) {
            return this.prices;
        }
        return this.prices.filter(p => !p.hidden);
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Product.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Product.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Product.prototype, "description", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], Product.prototype, "enabled", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 172 })
], Product.prototype, "hidden", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 173 })
], Product.prototype, "allowMultiple", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 173 })
], Product.prototype, "unique", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 172 })
], Product.prototype, "enableAfter", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 172 })
], Product.prototype, "disableAfter", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Image) })
], Product.prototype, "images", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(WebshopField), version: 94 })
], Product.prototype, "customFields", void 0);
__decorate([
    field({ decoder: new EnumDecoder(ProductType) })
], Product.prototype, "type", void 0);
__decorate([
    field({ decoder: ProductLocation, nullable: true, version: 105 })
], Product.prototype, "location", void 0);
__decorate([
    field({ decoder: ProductDateRange, nullable: true, version: 105 })
], Product.prototype, "dateRange", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 211 })
], Product.prototype, "seatingPlanId", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(ReservedSeat), nullable: true, version: 211 })
], Product.prototype, "reservedSeats", void 0);
__decorate([
    field({ decoder: BooleanDecoder, optional: true })
], Product.prototype, "askName", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 171 })
], Product.prototype, "maxPerOrder", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, optional: true })
], Product.prototype, "showStockBelow", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], Product.prototype, "stock", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], Product.prototype, "usedStock", void 0);
__decorate([
    field({
        decoder: new ArrayDecoder(ProductPrice),
        defaultValue: () => [
            ProductPrice.create({
                name: '',
                price: 0,
            }),
        ],
    })
], Product.prototype, "prices", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(OptionMenu) })
], Product.prototype, "optionMenus", void 0);
__decorate([
    field({ decoder: UitpasEventResponse, nullable: true, version: 378 })
], Product.prototype, "uitpasEvent", void 0);
//# sourceMappingURL=Product.js.map